import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate, todayStr } from "@/lib/format";
import {
  Wrench, Plus, Loader2, Trash2, MessageSquare, Send, Paperclip, ImagePlus,
  DoorClosed, CheckCircle2, AlertTriangle, Camera,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRIORITY = { high: { label: "High", cls: "bg-destructive/15 text-destructive" }, medium: { label: "Medium", cls: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]" }, low: { label: "Low", cls: "bg-secondary text-muted-foreground" } };
const STATUS = { open: "Open", in_progress: "In Progress", done: "Done" };

export default function Maintenance() {
  const { user } = useAuth();
  const canManage = can(user, ["admin", "regional", "finance", "hr", "gm", "employee"]);

  const [props, setProps] = useState([]);
  const { activePropertyId: pid, setActivePropertyId: setPid } = useActiveProperty();
  const [assignees, setAssignees] = useState([]);

  const [issues, setIssues] = useState(null);
  const [ooo, setOoo] = useState(null);

  useEffect(() => {
    api.get("/properties").then((r) => { setProps(r.data); if (!pid && r.data[0]) setPid(r.data[0].id); });
  }, []);

  const load = useCallback(() => {
    if (!pid) return;
    setIssues(null); setOoo(null);
    api.get("/maintenance", { params: { property_id: pid } }).then((r) => setIssues(r.data)).catch(() => setIssues([]));
    api.get("/ooo-rooms", { params: { property_id: pid } }).then((r) => setOoo(r.data)).catch(() => setOoo([]));
    api.get("/maintenance/assignees", { params: { property_id: pid } }).then((r) => setAssignees(r.data)).catch(() => setAssignees([]));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  // ---- create issue ----
  const [createOpen, setCreateOpen] = useState(false);
  const EMPTY = { title: "", location: "", description: "", priority: "medium", assigned_to: "", plan: "" };
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createIssue = async () => {
    if (!form.title.trim()) return toast.error("Enter a title");
    setSaving(true);
    try {
      const { data } = await api.post("/maintenance", { property_id: pid, ...form, assigned_to: form.assigned_to || null });
      if (photo) {
        const fd = new FormData(); fd.append("file", photo);
        await api.post(`/maintenance/${data.id}/photos`, fd);
      }
      toast.success("Issue created");
      setCreateOpen(false); setForm(EMPTY); setPhoto(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const patchIssue = async (id, body) => {
    try { const { data } = await api.patch(`/maintenance/${id}`, body); setIssues((its) => its.map((i) => (i.id === id ? data : i))); if (detail?.id === id) setDetail(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteIssue = async (id) => { if (!window.confirm("Delete this issue?")) return; await api.delete(`/maintenance/${id}`); setDetail(null); load(); };

  // ---- issue detail / thread ----
  const [detail, setDetail] = useState(null);
  const [cText, setCText] = useState("");
  const [cFile, setCFile] = useState(null);
  const [cKey, setCKey] = useState(0);
  const [cSaving, setCSaving] = useState(false);
  const detailData = issues?.find((i) => i.id === detail?.id) || detail;

  const postComment = async () => {
    if (!cText.trim() && !cFile) return toast.error("Add a note or photo");
    setCSaving(true);
    try {
      const fd = new FormData(); const params = new URLSearchParams({ text: cText });
      if (cFile) fd.append("file", cFile);
      await api.post(`/maintenance/${detail.id}/comments?${params.toString()}`, fd);
      setCText(""); setCFile(null); setCKey((k) => k + 1);
      const { data } = await api.get("/maintenance", { params: { property_id: pid } });
      setIssues(data); setDetail(data.find((i) => i.id === detail.id));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCSaving(false); }
  };
  const addDetailPhoto = async (f) => {
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/maintenance/${detail.id}/photos`, fd);
      const { data } = await api.get("/maintenance", { params: { property_id: pid } });
      setIssues(data); setDetail(data.find((i) => i.id === detail.id));
      toast.success("Photo added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---- OOO rooms ----
  const OOO_EMPTY = { room_number: "", issue: "", date_out: todayStr(), expected_return: "", notes: "" };
  const [oooForm, setOooForm] = useState(OOO_EMPTY);
  const [oooSaving, setOooSaving] = useState(false);
  const setO = (k, v) => setOooForm((f) => ({ ...f, [k]: v }));
  const addOoo = async () => {
    if (!oooForm.room_number.trim()) return toast.error("Enter a room number");
    setOooSaving(true);
    try { await api.post("/ooo-rooms", { property_id: pid, ...oooForm }); setOooForm(OOO_EMPTY); load(); toast.success("Room added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setOooSaving(false); }
  };
  const toggleOoo = async (r) => { const s = r.status === "ooo" ? "back" : "ooo"; const { data } = await api.patch(`/ooo-rooms/${r.id}`, { status: s }); setOoo((os) => os.map((x) => (x.id === r.id ? data : x))); };
  const saveOooNotes = async (r, notes) => { if ((notes || "") === (r.notes || "")) return; const { data } = await api.patch(`/ooo-rooms/${r.id}`, { notes }); setOoo((os) => os.map((x) => (x.id === r.id ? data : x))); };
  const deleteOoo = async (id) => { if (!window.confirm("Remove this room?")) return; await api.delete(`/ooo-rooms/${id}`); load(); };

  const openIssues = (issues || []).filter((i) => i.status !== "done").length;
  const oooCount = (ooo || []).filter((r) => r.status === "ooo").length;

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Communications</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Maintenance</h1>
          <p className="mt-1 text-sm text-muted-foreground">Log property issues with photos and a plan, and track out-of-order rooms.</p>
        </div>
        <Select value={pid} onValueChange={setPid}>
          <SelectTrigger className="w-56" data-testid="maint-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
          <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="issues">
        <TabsList>
          <TabsTrigger value="issues" data-testid="tab-issues"><Wrench className="mr-2 h-4 w-4" /> Issues{openIssues ? ` (${openIssues})` : ""}</TabsTrigger>
          <TabsTrigger value="ooo" data-testid="tab-ooo"><DoorClosed className="mr-2 h-4 w-4" /> OOO Rooms{oooCount ? ` (${oooCount})` : ""}</TabsTrigger>
        </TabsList>

        {/* ISSUES */}
        <TabsContent value="issues" className="mt-6 space-y-4">
          {canManage && (
            <div className="flex justify-end">
              <Button onClick={() => setCreateOpen(true)} disabled={!pid} data-testid="new-issue-btn"><Plus className="mr-2 h-4 w-4" /> New Issue</Button>
            </div>
          )}
          {issues === null ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : issues.length === 0 ? (
            <Card className="rounded-none border border-dashed p-12 text-center">
              <Wrench className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">No maintenance issues logged.</p>
            </Card>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {issues.map((it) => (
                <Card key={it.id} className="rounded-none border p-4" data-testid={`issue-${it.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-bold ${PRIORITY[it.priority]?.cls}`}>{PRIORITY[it.priority]?.label}</span>
                        <h3 className="truncate font-heading text-base font-bold">{it.title}</h3>
                      </div>
                      {it.location && <div className="mt-0.5 text-xs text-muted-foreground">📍 {it.location}</div>}
                    </div>
                    <Select value={it.status} onValueChange={(v) => patchIssue(it.id, { status: v })}>
                      <SelectTrigger className="h-8 w-32 shrink-0 text-xs" data-testid={`issue-status-${it.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(STATUS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  {it.description && <p className="mt-2 line-clamp-2 text-sm">{it.description}</p>}
                  {it.photos?.length > 0 && (
                    <div className="mt-3 flex gap-2 overflow-x-auto">
                      {it.photos.slice(0, 4).map((p) => (
                        <img key={p.id} src={`${API_BASE}/maintenance/${it.id}/photos/${p.id}`} alt="" className="h-16 w-16 shrink-0 border border-border object-cover" />
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{it.assigned_to_name ? `Assigned: ${it.assigned_to_name}` : "Unassigned"}</span>
                    <Button size="sm" variant="outline" onClick={() => { setDetail(it); setCText(""); setCFile(null); }} data-testid={`issue-open-${it.id}`}>
                      <MessageSquare className="mr-1 h-3.5 w-3.5" /> Details{it.comments?.length ? ` (${it.comments.length})` : ""}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* OOO ROOMS */}
        <TabsContent value="ooo" className="mt-6 space-y-4">
          {canManage && (
            <Card className="rounded-none border p-4" data-testid="ooo-form">
              <div className="grid gap-3 md:grid-cols-6">
                <div className="space-y-1.5"><Label>Room #</Label><Input value={oooForm.room_number} onChange={(e) => setO("room_number", e.target.value)} data-testid="ooo-room" /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Issue</Label><Input value={oooForm.issue} onChange={(e) => setO("issue", e.target.value)} data-testid="ooo-issue" /></div>
                <div className="space-y-1.5"><Label>Date Out</Label><Input type="date" value={oooForm.date_out} onChange={(e) => setO("date_out", e.target.value)} data-testid="ooo-dateout" /></div>
                <div className="space-y-1.5"><Label>Expected Return</Label><Input type="date" value={oooForm.expected_return} onChange={(e) => setO("expected_return", e.target.value)} data-testid="ooo-return" /></div>
                <div className="flex items-end"><Button className="w-full" onClick={addOoo} disabled={oooSaving} data-testid="ooo-add-btn">{oooSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button></div>
              </div>
              <div className="mt-3 space-y-1.5"><Label>Notes</Label><Input value={oooForm.notes} onChange={(e) => setO("notes", e.target.value)} data-testid="ooo-notes" /></div>
            </Card>
          )}
          {ooo === null ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : ooo.length === 0 ? (
            <Card className="rounded-none border border-dashed p-12 text-center">
              <DoorClosed className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">No out-of-order rooms.</p>
            </Card>
          ) : (
            <Card className="rounded-none border">
              <div className="overflow-auto">
                <Table data-testid="ooo-table">
                  <TableHeader><TableRow>
                    <TableHead className="w-20">Room #</TableHead><TableHead>Issue</TableHead>
                    <TableHead className="w-28">Date Out</TableHead><TableHead className="w-28">Exp. Return</TableHead>
                    <TableHead className="w-28">Status</TableHead><TableHead className="min-w-[200px]">Notes</TableHead>
                    {canManage && <TableHead className="w-10" />}
                  </TableRow></TableHeader>
                  <TableBody>
                    {ooo.map((r) => (
                      <TableRow key={r.id} data-testid={`ooo-row-${r.id}`} className={r.status === "ooo" ? "bg-destructive/5" : ""}>
                        <TableCell className="font-mono font-semibold">{r.room_number}</TableCell>
                        <TableCell className="text-sm">{r.issue || "—"}</TableCell>
                        <TableCell className="text-xs">{r.date_out ? fmtDate(r.date_out) : "—"}</TableCell>
                        <TableCell className="text-xs">{r.expected_return ? fmtDate(r.expected_return) : "—"}</TableCell>
                        <TableCell>
                          <button onClick={() => canManage && toggleOoo(r)} disabled={!canManage} className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold ${r.status === "ooo" ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`} data-testid={`ooo-status-${r.id}`}>
                            {r.status === "ooo" ? <><AlertTriangle className="h-3 w-3" /> OOO</> : <><CheckCircle2 className="h-3 w-3" /> Back</>}
                          </button>
                        </TableCell>
                        <TableCell>
                          {canManage ? (
                            <Input defaultValue={r.notes || ""} className="h-8 text-xs" onBlur={(e) => saveOooNotes(r, e.target.value)} data-testid={`ooo-note-${r.id}`} />
                          ) : (r.notes || "—")}
                        </TableCell>
                        {canManage && <TableCell><Button size="icon" variant="ghost" onClick={() => deleteOoo(r.id)} data-testid={`ooo-delete-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create issue dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-issue-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Maintenance Issue</DialogTitle><DialogDescription>Describe the issue, upload a photo, and set a plan.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title *</Label><Input value={form.title} onChange={(e) => setF("title", e.target.value)} data-testid="issue-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Location / Room</Label><Input value={form.location} onChange={(e) => setF("location", e.target.value)} placeholder="e.g. Room 214 bathroom" data-testid="issue-location" /></div>
              <div className="space-y-1.5"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setF("priority", v)}><SelectTrigger data-testid="issue-priority"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Assign To</Label>
              <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setF("assigned_to", v === "unassigned" ? "" : v)}>
                <SelectTrigger data-testid="issue-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{assignees.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name} · {a.role}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setF("description", e.target.value)} data-testid="issue-description" /></div>
            <div className="space-y-1.5"><Label>Plan / Action</Label><Textarea rows={2} value={form.plan} onChange={(e) => setF("plan", e.target.value)} placeholder="How will this be fixed?" data-testid="issue-plan" /></div>
            <div className="space-y-1.5"><Label className="flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> Photo</Label>
              <Input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="issue-photo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createIssue} disabled={saving} data-testid="issue-save-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / thread dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="issue-detail-dialog">
          {detailData && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading">
                  <span className={`px-2 py-0.5 text-xs font-bold ${PRIORITY[detailData.priority]?.cls}`}>{PRIORITY[detailData.priority]?.label}</span>
                  {detailData.title}
                </DialogTitle>
                <DialogDescription>{detailData.location || "No location"} · reported by {detailData.created_by}</DialogDescription>
              </DialogHeader>
              <div className="max-h-[55vh] space-y-4 overflow-auto pr-1">
                {detailData.description && <div><div className="label-caps text-muted-foreground">Description</div><p className="mt-1 whitespace-pre-wrap text-sm">{detailData.description}</p></div>}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label className="text-xs">Priority</Label>
                    <Select value={detailData.priority} onValueChange={(v) => patchIssue(detailData.id, { priority: v })}><SelectTrigger className="h-8 text-xs" data-testid="detail-priority"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
                  <div className="space-y-1.5"><Label className="text-xs">Assign To</Label>
                    <Select value={detailData.assigned_to || "unassigned"} onValueChange={(v) => patchIssue(detailData.id, { assigned_to: v === "unassigned" ? "" : v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid="detail-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{assignees.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name} · {a.role}</SelectItem>))}</SelectContent></Select></div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Plan / Action</Label>
                  <Textarea rows={2} defaultValue={detailData.plan || ""} onBlur={(e) => patchIssue(detailData.id, { plan: e.target.value })} placeholder="How will this be fixed?" data-testid="detail-plan" />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <div className="label-caps text-muted-foreground">Photos</div>
                    <label className="flex cursor-pointer items-center gap-1 text-xs text-primary hover:underline">
                      <ImagePlus className="h-3.5 w-3.5" /> Add photo
                      <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={(e) => addDetailPhoto(e.target.files?.[0])} data-testid="detail-add-photo" />
                    </label>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(detailData.photos || []).length === 0 && <span className="text-xs text-muted-foreground">No photos yet.</span>}
                    {(detailData.photos || []).map((p) => (
                      <a key={p.id} href={`${API_BASE}/maintenance/${detailData.id}/photos/${p.id}`} target="_blank" rel="noreferrer">
                        <img src={`${API_BASE}/maintenance/${detailData.id}/photos/${p.id}`} alt="" className="h-20 w-20 border border-border object-cover" />
                      </a>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="label-caps text-muted-foreground">Updates</div>
                  <div className="mt-2 space-y-2">
                    {(detailData.comments || []).length === 0 && <p className="text-xs text-muted-foreground">No updates yet.</p>}
                    {(detailData.comments || []).map((c) => (
                      <div key={c.id} className="border-l-2 border-border px-3 py-1.5" data-testid={`maint-comment-${c.id}`}>
                        <div className="flex items-center justify-between text-xs"><span className="font-semibold">{c.user_name} <span className="font-normal capitalize text-muted-foreground">· {c.role}</span></span><span className="text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span></div>
                        {c.text && <p className="mt-0.5 whitespace-pre-wrap text-sm">{c.text}</p>}
                        {c.file && <a href={`${API_BASE}/maintenance/${detailData.id}/comments/${c.id}/file`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"><Paperclip className="h-3 w-3" /> {c.file.filename || "Attachment"}</a>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2 border-t border-border pt-3">
                <Textarea rows={2} value={cText} onChange={(e) => setCText(e.target.value)} placeholder="Post an update…" data-testid="maint-comment-input" />
                <div className="flex items-center justify-between gap-2">
                  <Input key={cKey} type="file" accept=".jpg,.jpeg,.png,.pdf" className="max-w-[55%]" onChange={(e) => setCFile(e.target.files?.[0] || null)} data-testid="maint-comment-file" />
                  <div className="flex gap-2">
                    {(user?.role === "admin" || user?.role === "regional" || detailData.created_by_id === (user?.id)) && (
                      <Button variant="ghost" size="icon" onClick={() => deleteIssue(detailData.id)} data-testid="issue-delete-btn"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    )}
                    <Button onClick={postComment} disabled={cSaving} data-testid="maint-comment-send">{cSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Post</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
